// Copyright © 2019-2026 Focal Systems. All rights reserved.

import createClient from "openapi-fetch";
import { showApiErrorToast } from "@/api/api-error-toast";
import type { paths } from "@/api/schema";
import { addTelemetryRecord, isDebugModeEnabled } from "@/lib/debug-mode";
import { setUnreadCount } from "@/lib/notifications/unread-count-store";
import { getShowHttpErrorToasts } from "@/lib/preferences";
import { DEFAULT_AUTHENTICATED_PATH } from "@/lib/routes";

const API_BASE_URL = import.meta.env.VITE_API_URL;

/**
 * Build the `/login` URL for a 401, preserving the current page as `?redirect`
 * so re-login returns the user where they were. Mirrors `loginRedirectSearch`
 * in `_authenticated.tsx`: we omit `?redirect` when the target would resolve to
 * the default landing page anyway (bare root, the login page, or the default
 * page with no query), since login falls back there regardless.
 */
function loginUrlPreservingLocation(): string {
  const here = window.location.pathname + window.location.search;
  if (window.location.pathname === "/" || window.location.pathname === "/login") return "/login";
  if (here === DEFAULT_AUTHENTICATED_PATH) return "/login";
  return `/login?redirect=${encodeURIComponent(here)}`;
}

// NB: do NOT set a global "Content-Type" default here. openapi-fetch already
// applies "application/json" for JSON bodies and omits it for FormData so the
// browser can set the "multipart/form-data; boundary=…" header. A global default
// clobbers that boundary and breaks file uploads (e.g. OTA versions → 422).
export const api = createClient<paths>({
  baseUrl: API_BASE_URL,
});

export function getAuthToken(): string | null {
  return localStorage.getItem("access_token");
}

// Telemetry middleware — fires toasts in debug mode
const timings = new Map<string, number>();

api.use({
  onRequest({ request }) {
    timings.set(request.url, performance.now());
    return request;
  },
  onResponse({ request, response }) {
    if (!isDebugModeEnabled()) return response;
    const start = timings.get(request.url);
    const ms = start !== undefined ? Math.round(performance.now() - start) : null;
    timings.delete(request.url);
    const url = new URL(request.url);
    addTelemetryRecord({
      method: request.method,
      pathname: url.pathname,
      search: url.search,
      status: response.status,
      duration: ms,
    });
    return response;
  },
  onError({ request }) {
    if (!isDebugModeEnabled()) return undefined;
    const start = timings.get(request.url);
    const ms = start !== undefined ? Math.round(performance.now() - start) : null;
    timings.delete(request.url);
    const url = new URL(request.url);
    addTelemetryRecord({
      method: request.method,
      pathname: url.pathname,
      search: url.search,
      status: 0,
      duration: ms,
    });
    return undefined;
  },
});

// Global error toast for 422 / 5xx responses and network errors
const requestBodies = new Map<string, string>();

api.use({
  async onRequest({ request }) {
    if (request.body && ["POST", "PATCH", "PUT"].includes(request.method)) {
      const body = await request.clone().text();
      requestBodies.set(request.url, body);
    }
    return request;
  },
  onResponse({ request, response }) {
    const requestBody = requestBodies.get(request.url);
    requestBodies.delete(request.url);

    if ((response.status === 422 || response.status >= 500) && getShowHttpErrorToasts()) {
      let formattedRequestBody: string | undefined;
      if (requestBody) {
        try {
          formattedRequestBody = JSON.stringify(JSON.parse(requestBody), null, 2);
        } catch {
          formattedRequestBody = requestBody;
        }
      }

      response
        .clone()
        .text()
        .then((body) => {
          let formatted: string;
          try {
            formatted = JSON.stringify(JSON.parse(body), null, 2);
          } catch {
            formatted = body;
          }
          showApiErrorToast({
            method: request.method,
            url: request.url,
            status: response.status,
            body: formatted,
            requestBody: formattedRequestBody,
          });
        })
        .catch(() => {
          showApiErrorToast({
            method: request.method,
            url: request.url,
            status: response.status,
            body: "(unable to read response body)",
            requestBody: formattedRequestBody,
          });
        });
    }
    return response;
  },
  onError({ request, error }) {
    const requestBody = requestBodies.get(request.url);
    requestBodies.delete(request.url);

    if (!getShowHttpErrorToasts()) return undefined;

    let formattedRequestBody: string | undefined;
    if (requestBody) {
      try {
        formattedRequestBody = JSON.stringify(JSON.parse(requestBody), null, 2);
      } catch {
        formattedRequestBody = requestBody;
      }
    }

    showApiErrorToast({
      method: request.method,
      url: request.url,
      status: 0,
      body: error instanceof Error ? error.message : String(error),
      requestBody: formattedRequestBody,
    });
    return undefined;
  },
});

// Read unread notification count from response header
api.use({
  onResponse({ response }) {
    const unread = response.headers.get("X-Unread-Notifications");
    if (unread !== null) {
      const count = parseInt(unread, 10);
      if (!Number.isNaN(count)) setUnreadCount(count);
    }
    return response;
  },
});

// Inject auth token on every request
api.use({
  onRequest({ request }) {
    const token = localStorage.getItem("access_token");
    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }
    return request;
  },
  onResponse({ response }) {
    if (response.status === 401) {
      localStorage.removeItem("access_token");
      window.location.href = loginUrlPreservingLocation();
    }
    return response;
  },
});
